import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Send, X, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const avatarUrl = (name) =>
  `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(name || "player")}&backgroundType=gradientLinear`;

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function ChatPanel({ open, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const atBottom = useRef(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/messages`);
      setMessages(data.messages || []);
      setOnline(data.online || 0);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (atBottom.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const send = async () => {
    const msg = text.trim();
    if (!msg) return;
    if (!user) { window.dispatchEvent(new Event("uc:open-auth")); return; }
    setSending(true);
    const token = localStorage.getItem("uc_token");
    try {
      const { data } = await axios.post(`${API}/chat/message`, { message: msg },
        { headers: { Authorization: `Bearer ${token}` } });
      atBottom.current = true;
      setMessages((m) => [...m, data.message]);
      setText("");
    } catch (_) { /* ignore */ } finally { setSending(false); }
  };

  return (
    <aside
      data-testid="live-chat-panel"
      className={`fixed right-0 top-0 bottom-0 z-40 flex w-[86vw] max-w-[340px] flex-col border-l border-[var(--uc-border)] bg-[var(--uc-chat)] transition-transform duration-300 lg:z-30
        ${open ? "translate-x-0" : "translate-x-full lg:translate-x-full"}`}
    >
      <div className="flex items-center justify-between border-b border-[var(--uc-border)] px-4 py-3.5">
        <div className="flex items-center gap-2">
          <span className="font-heading text-sm font-bold text-white">Chat</span>
          <span className="flex items-center gap-1 rounded-full bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-[var(--uc-green)]">
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-[var(--uc-green)]" />
            {online.toLocaleString()}
          </span>
        </div>
        <button onClick={onClose} data-testid="chat-close-btn" aria-label="Close chat"
          className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--uc-muted)] hover:bg-white/5 hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div ref={listRef} onScroll={onScroll} data-testid="live-chat-messages"
        className="flex-1 space-y-3 overflow-y-auto px-3 py-4">
        {messages.map((m) => (
          <div key={m.id} data-testid="chat-message" className="flex gap-2.5">
            <img src={avatarUrl(m.user)} alt="" className="h-8 w-8 shrink-0 rounded-full bg-[var(--uc-bubble)]" />
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="truncate text-xs font-bold text-[#C4B5FD]">{m.user}</span>
                <span className="text-[10px] text-[var(--uc-muted)]">{fmtTime(m.ts)}</span>
              </div>
              <div className="mt-0.5 inline-block max-w-full break-all rounded-xl rounded-tl-sm bg-[var(--uc-bubble)] px-3 py-1.5 text-[13px] leading-snug text-[var(--uc-text)] [overflow-wrap:anywhere]">
                {m.text}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-[var(--uc-border)] p-3">
        {!user && (
          <button onClick={() => window.dispatchEvent(new Event("uc:open-auth"))}
            className="mb-2 flex w-full items-center justify-center gap-1.5 rounded-xl bg-white/5 py-2 text-xs font-semibold text-[var(--uc-muted)] hover:text-white">
            <Users className="h-3.5 w-3.5" /> Sign in to join the chat
          </button>
        )}
        <div className="flex items-center gap-2">
          <input
            data-testid="live-chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder={user ? "Say something…" : "Sign in to chat"}
            disabled={!user}
            maxLength={300}
            className="flex-1 rounded-xl border border-[var(--uc-border)] bg-[var(--uc-bubble)] px-3 py-2.5 text-sm text-white placeholder:text-[var(--uc-muted)] focus:border-[var(--uc-border-active)] focus:outline-none disabled:opacity-60"
          />
          <button
            data-testid="live-chat-send-button"
            onClick={send}
            disabled={sending || !text.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-lg shadow-purple-900/30 transition-all hover:from-[#7C3AED] hover:to-[#5B21B6] disabled:opacity-40">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
